import {
  Container,
  Header,
  Spinner,
  Table
} from '@amzn/awsui-components-react';
import { ColumnSetting } from 'common/utils/config-models';
import { FunctionComponent, useEffect, useState } from 'react';

import ordersTableConfig from './order-sublineitems.table-config.json';
import { AddSubLineItemCommandInput, OrderSubLineItem, UpdateSubLineItemCommandInput, UpdateSubLineItemCommandOutput } from '@amzn/ito-client';
import { buildColumnDefinitions, enhanceColumnDefinitionsForRowEditing, getString, renderEditableRow } from 'common';
import TableActions from './TableActions';
import EmptyTableBox from 'common/EmptyTableBox/EmptyTableBox';
import { setNestedProperty } from '../../OrderSummaryTab/useOrderSummaryForm';
import { CSRFService } from 'services/csrf-service';
import { useAddOrderSublineItem, useUpdateSubLineItemDetails } from 'hooks';
import { useFlashBarItemsActions } from 'common/UseFlashBarItems/useFlashBarItems';
import { useOrderEditActions, useOrderEditItems } from 'views/OrderDetailsSingleViewPage/useOrderEdit';
import { DocumentType } from '@smithy/types';
import nonCoupaList from "./nonCoupaCostCenters.json";
import { LINE_ITEMS_ERROR_UI_STRING_PATH } from 'interfaces/constants';
import { HardwareOrderStatus } from 'interfaces';
import { DownloadLoading } from "../../../../../common/DownloadLoading/DownloadLoading";

const columns: ColumnSetting[] = ordersTableConfig.columns;

const MAIN_ERROR_MESSAGE = getString(`${LINE_ITEMS_ERROR_UI_STRING_PATH}.errorMessage`);

export interface OrdersSubLineItemsTableProps {
  useOrderEditItems: useOrderEditItems,
  useOrderEditActions: useOrderEditActions,
  reloadOrder: (id: string | undefined) => void,
  flashBarItemsActions: useFlashBarItemsActions,
  itemName?: string;
}

export const OrderSubLineItemsTable: FunctionComponent<OrdersSubLineItemsTableProps> = (props: OrdersSubLineItemsTableProps) => {
  // Items
  const orderSelectedId = props.useOrderEditItems.orderSelected?.orderId;
  const lineItemSelected = props.useOrderEditItems.lastLineItemSelected;

  // Actions
  const [loading,] = useState(false);
  const [editedValues, setEditedValues] = useState<Record<string, Record<string, any>>>({});
  const [savingRowIds, setSavingRowIds] = useState<Set<string>>(new Set());

  // Build base column definitions
  const baseColumnDefinitions = buildColumnDefinitions(columns, undefined, props.useOrderEditItems.canBeEdited);
  const { data: updateData, error: updateError, doUpdateSubLineItemAsync } = useUpdateSubLineItemDetails();
  const { response: addData, error: addError, doAddSublineItemAsync } = useAddOrderSublineItem();

  // Check if Subline Items is empty, to render empty row in table
  const renderEmptyRow = !lineItemSelected?.subLineItems || lineItemSelected?.subLineItems.length < 1 ;

  /**
   * Validates all required fields before form submission
   * @returns {object} An object containing validation errors and a boolean indicating if the form is valid
   */
  const validateSubmit = (item: OrderSubLineItem) => {
    // Validate required fields
    const validationErrors: Record<string, string> = {};
    const itemId = item.id || '';
    const itemEditedValues = editedValues[itemId] || {};

    columns.forEach(column => {
      // Get the current value, either from editedValues or from the original item
      const currentValue = itemEditedValues[column.id] !== undefined
        ? itemEditedValues[column.id]
        : item[column.id as keyof OrderSubLineItem];

      // Check if this is a required field (based on validation config)
      const isRequired = column.editConfig?.validation === "validateRequiredField";

      // Validate required fields
      if (isRequired) {
        if (!currentValue ||
          (typeof currentValue === "string" && currentValue.trim() === "")) {
          validationErrors[column.id] = `${column.header} ${getString('orderDetails.tabs.itemsRequested.banners.errors.isRequired')}`;
        }
      }
    });

    // Additional business rule validations
    const status = itemEditedValues.status !== undefined
      ? itemEditedValues.status
      : item.status;

    const poNumber = itemEditedValues.poNumber !== undefined
      ? itemEditedValues.poNumber
      : item.poNumber;

    const quantityDelivered = itemEditedValues.quantityDelivered !== undefined
      ? itemEditedValues.quantityDelivered
      : item.quantityDelivered;

    const poValidation: boolean = !poNumber || poNumber.trim() === '';

    // Status can't be PENDING_DELIVERY if there is no poNumber
    if (status === HardwareOrderStatus.PENDING_DELIVERY && poValidation) {
      validationErrors['status'] = getString('orderDetails.tabs.itemsRequested.banners.errors.pendingDeliveryValidation');
    }

    // Status can't be DELIVERED if there is no poNumber and/or Quantity is < 1
    if (status === HardwareOrderStatus.DELIVERED &&
      (poValidation || (!quantityDelivered || quantityDelivered < 1))) {
      validationErrors['status'] = getString('orderDetails.tabs.itemsRequested.banners.errors.deliveryValidation');
    }

    return {
      validationErrors,
      isValid: Object.keys(validationErrors).length === 0
    };
  }

  // useEffect for subline item updates
  useEffect(() => {
    handleOperationResult(updateData, updateError, getString('orderDetails.tabs.itemsRequested.banners.success.lineItemUpdated'), props.useOrderEditActions.flagOrderWithFirstEdit);
  }, [updateData, updateError]);

  // useEffect for new subline item from empty row
  useEffect(() => {
    handleOperationResult(addData, addError, getString('orderDetails.tabs.itemsRequested.banners.success.sublineItemAdded'));
  }, [addData, addError]);

  const handleOperationResult = (data: AddSubLineItemCommandInput | UpdateSubLineItemCommandOutput | undefined, error: Error | null, successMessage: string, additionalSuccessActions = () => {}) => {
    if (data && !error) {
      props.reloadOrder(orderSelectedId);
      additionalSuccessActions();

      props.flashBarItemsActions.addSuccessFlashBarItem(successMessage);
    } else if (error) {
      props.flashBarItemsActions.addErrorFlashBarItem(
        `${MAIN_ERROR_MESSAGE} ${error instanceof Error ? error.message : String(error)}`
      );
    }
  };

  // Row editing handlers
  const handleClearEdit = (itemId: string) => {
    setEditedValues(prev => {
      const newValues = { ...prev };
      delete newValues[itemId];
      return newValues;
    });
    setSavingRowIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(itemId);
      return newSet;
    });
  };

  const handleSaveRowEdit = async (item: OrderSubLineItem) => {
    // Validate all required fields before saving
    const { validationErrors, isValid } = validateSubmit(item);

    // If there are validation errors, show them and prevent submission
    if (!isValid) {
      const errorMessages = Object.values(validationErrors).join(', ');
      props.flashBarItemsActions.addErrorFlashBarItem(`${MAIN_ERROR_MESSAGE} ${errorMessages}`);
      return;
    }

    const itemId = item.id || '';
    setSavingRowIds(prev => new Set(prev).add(itemId));

    try {
      // Create update props
      const itemUpdated = { ...item };
      const itemEditedValues = editedValues[itemId] || {};

      const csrfService = new CSRFService();
      const token = await csrfService.getToken();

      // Create new item
      if (item.id === "new-subline-item") {
        const { id, ...itemWithoutId } = item;

        const addProps: AddSubLineItemCommandInput = {
          orderId: orderSelectedId,
          itemId: lineItemSelected?.itemId,
          subLineItem: {...itemWithoutId, ...itemEditedValues}
        };

        // Add new subline item
        await doAddSublineItemAsync(addProps, token);

      } else {
        // Apply all edited values
        Object.entries(itemEditedValues).forEach(([key, value]) => {
          setNestedProperty(itemUpdated, key as keyof OrderSubLineItem, value);
        });

        const updateProps: UpdateSubLineItemCommandInput = {
          orderId: orderSelectedId,
          itemId: lineItemSelected?.itemId,
          id: itemId,
          subLineItem: itemUpdated
        };

        // Save changes
        await doUpdateSubLineItemAsync(updateProps, token);
      }
      // Clear editing state after successful save
      handleClearEdit(itemId);
    } catch (error) {
      props.flashBarItemsActions.addErrorFlashBarItem(`${MAIN_ERROR_MESSAGE} ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const handleFieldChange = (itemId: string, fieldName: string, value: any) => {
    setEditedValues(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [fieldName]: value
      }
    }));
  };

  const isNonCoupaCostCenter = (details: DocumentType | undefined): boolean => {
    return (
      !!details &&
      typeof details === 'object' &&
      'costCenter' in details &&
      typeof details.costCenter === 'string' &&
      nonCoupaList.includes(details.costCenter)
    );
  }

  if (loading) {
    return (
        <DownloadLoading/>
    );
  }

  const hasSubLineItems = lineItemSelected?.subLineItems;
  const details = props.useOrderEditItems.currentOrder?.details;

  // Filter column definitions based on conditions
  let filteredColumnDefinitions = [...baseColumnDefinitions];

  // Remove PO/PR Links from cost centers that do not use coupa
  if (!hasSubLineItems || renderEmptyRow || isNonCoupaCostCenter(details)) {
    filteredColumnDefinitions = filteredColumnDefinitions.filter((obj) => obj.id !== "prNumberLink");
    filteredColumnDefinitions = filteredColumnDefinitions.filter((obj) => obj.id !== "poNumberLink");
  } else if ((hasSubLineItems.every((obj) => obj.poNumber === ""))) {
    filteredColumnDefinitions = filteredColumnDefinitions.filter((obj) => obj.id !== "poNumberLink");
  }

  if (renderEmptyRow) {
    filteredColumnDefinitions = filteredColumnDefinitions.filter((obj) => obj.id !== "status");
  }

  // Apply row editing if enabled - use always-editable mode
  const columnDefinitions = props.useOrderEditItems.canBeEdited
    ? enhanceColumnDefinitionsForRowEditing(
      columns,
      filteredColumnDefinitions,
      {
        editedValues,
        handleFieldChange
      },
      {
        savingRowId: undefined,
        onEdit: () => { },
        onCancel: () => { },
        onSave: () => { },
        onSaveWithItem: handleSaveRowEdit,
        TableActions: (props: any) => (
          <TableActions
            {...props}
            savingRowIds={savingRowIds}
            testIdPart="subline-items"
            buttonText={renderEmptyRow ? "addButton" : "saveButton"}
          />
        ),
        editMode: 'alwaysEditable'
      }
    )
    : filteredColumnDefinitions;

  const emptyRowData = { id: "new-subline-item", carrier: "", deliveryDate: undefined, poNumber: "", prNumber: "", quantityDelivered: 0, trackingNumber: "", vendor: "" };
  const subLineItems = renderEmptyRow ? [emptyRowData] : lineItemSelected?.subLineItems!;

  return (
    <Table
      data-testid="order-sub-line-items-table"
      empty={<EmptyTableBox />}
      header={
        <Header>{props.itemName ?? getString('orderDetails.tabs.itemsRequested.table.SublineItemsTableHeader')}</Header>
      }
      columnDefinitions={columnDefinitions}
      loadingText={getString('common.loading')}
      items={subLineItems}
      resizableColumns={true}
      wrapLines
    />
  );
};
